import { useEffect, type RefObject } from "react";

/**
 * Clip-mode boundary watcher — keeps a `<video>` playing only the
 * `[start, end]` window of a clip.
 *
 * Ports the legacy `_loadFeedbackVideoForClip` clip monitor
 * (`js/coaching/feedback-player.js:399-537`). A clip has no MP4
 * export — playback is seek-based against the source match video,
 * with this watcher enforcing the boundaries cooperatively:
 *
 *   - **On `loadedmetadata`** — seek to `start`. No autoplay; the
 *     coach presses Play when ready (consistent with note
 *     playback).
 *   - **On `timeupdate` + a 250ms fallback interval** (some HLS
 *     sources throttle `timeupdate` during seeks/stalls) — when
 *     the playhead crosses `end`, pause + snap to `end` + raise an
 *     `atEnd` flag. If the playhead lands before `start`, snap back
 *     to `start`.
 *   - **On `play`** — if `atEnd` (snap-paused) OR the playhead is
 *     already past `end` (user scrubbed past then hit Play), rewind
 *     to `start` so the next frame is the clip's beginning, not the
 *     locked end. If the playhead is before `start`, snap to `start`.
 *     This is what stops the recursive pause/play loop.
 *   - **On `seeked`** — if the user scrubbed before `start`, snap
 *     back to `start`; otherwise if they scrubbed BACKWARD to before
 *     `end`, clear `atEnd` so normal playback resumes (otherwise a
 *     scrub-back would still yank them to `start` on the next play).
 *
 * The `atEnd` flag is per-session state held in a ref, not React
 * state — it changes on every boundary event and nothing renders
 * off it. The session is re-armed whenever `videoRef.current`,
 * `start`, `end`, or `enabled` changes; teardown removes the exact
 * listener function objects + clears the interval.
 *
 * Tolerance: 0.05s on the boundary checks — a clip's end is
 * enforced cooperatively, not sample-accurately.
 */

const BOUNDARY_TOLERANCE = 0.05;
const FALLBACK_INTERVAL_MS = 250;

export type UseClipBoundaryWatcherOptions = {
  /** The `<video>` element to constrain. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Clip window start, in seconds. */
  start: number;
  /** Clip window end, in seconds. Must be > start; the hook
   * defensively floors it to `start + 0.5` if not. */
  end: number;
  /** When false the watcher doesn't arm — used so the modal can
   * mount the hook unconditionally and toggle it by mode. */
  enabled: boolean;
  /** Preserve every valid canonical positive window; legacy minimum stays default. */
  exactWindow?: boolean;
};

export function useClipBoundaryWatcher({
  videoRef,
  start,
  end,
  enabled,
  exactWindow = false,
}: UseClipBoundaryWatcherOptions): void {
  // Track the element identity so the effect re-arms when the
  // <video> behind the ref swaps (HlsVideo remounts on src change).
  const el = videoRef.current;

  useEffect(() => {
    if (!enabled || !el) return;

    const startTime = Math.max(0, start);
    if (exactWindow && (!Number.isFinite(start) || !Number.isFinite(end) || end <= startTime)) return;
    const endTime = exactWindow ? end : Math.max(startTime + 0.5, end);
    const tolerance = exactWindow ? Math.min(BOUNDARY_TOLERANCE, (endTime - startTime) / 10) : BOUNDARY_TOLERANCE;

    // Per-session state. `atEnd` is the signal the `play` handler
    // reads to know it must rewind before allowing playback.
    let atEnd = false;

    // Seek to the clip start once metadata is ready. No autoplay.
    const onLoadedMetadata = () => {
      el.currentTime = startTime;
    };
    // If metadata is ALREADY loaded (element reused), seek now.
    if (el.readyState >= 1 /* HAVE_METADATA */) {
      el.currentTime = startTime;
    }

    const enforceBoundary = () => {
      if (!el.isConnected) return;
      if (el.currentTime < startTime - tolerance) {
        el.currentTime = startTime;
        atEnd = false;
        return;
      }
      if (el.currentTime >= endTime - tolerance && !el.paused) {
        el.pause();
        el.currentTime = endTime;
        atEnd = true;
      }
    };

    const onPlay = () => {
      if (el.currentTime < startTime - tolerance) {
        atEnd = false;
        el.currentTime = startTime;
        return;
      }
      // Snap-paused at the boundary, OR the user scrubbed past end
      // then pressed Play — either way, rewind to start.
      if (atEnd || el.currentTime >= endTime - tolerance) {
        atEnd = false;
        el.currentTime = startTime;
      }
    };

    const onSeeked = () => {
      if (el.currentTime < startTime - tolerance) {
        atEnd = false;
        el.currentTime = startTime;
        return;
      }
      // Scrubbed backward to before the end → clear the at-end
      // flag so normal playback resumes from wherever they landed.
      if (el.currentTime < endTime - tolerance) {
        atEnd = false;
      }
    };

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", enforceBoundary);
    el.addEventListener("play", onPlay);
    el.addEventListener("seeked", onSeeked);
    // Belt-and-braces: HLS can throttle `timeupdate` during seeks /
    // buffer stalls, so a fallback interval also enforces the
    // boundary.
    const intervalId = window.setInterval(enforceBoundary, FALLBACK_INTERVAL_MS);

    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", enforceBoundary);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("seeked", onSeeked);
      window.clearInterval(intervalId);
    };
  }, [el, start, end, enabled, exactWindow]);
}

/**
 * Pure boundary-decision helper — extracted so the watcher's
 * behavior is unit-testable without a DOM `<video>`. Given the
 * current playhead + session state + the window, returns what the
 * watcher should do. The hook above wires the same three rules to
 * real media events; this function IS those rules.
 *
 * Returns one of:
 *   - `{ action: "snap-start" }` — playhead landed before `start`
 *     → seek to `start` + clear `atEnd`.
 *   - `{ action: "pause-snap" }` — playhead crossed `end` while
 *     playing → pause + snap to `end` + set `atEnd`.
 *   - `{ action: "rewind" }` — `play` fired while at/past the end
 *     → seek to `start` + clear `atEnd`.
 *   - `{ action: "clear-at-end" }` — `seeked` landed before `end`
 *     → clear `atEnd`.
 *   - `{ action: "none" }` — nothing to do.
 */
export type ClipBoundaryEvent = "timeupdate" | "play" | "seeked";

export type ClipBoundaryDecision =
  | { action: "snap-start" }
  | { action: "pause-snap" }
  | { action: "rewind" }
  | { action: "clear-at-end" }
  | { action: "none" };

export function decideClipBoundary(
  event: ClipBoundaryEvent,
  state: { currentTime: number; paused: boolean; atEnd: boolean },
  window: { start: number; end: number; exactWindow?: boolean },
): ClipBoundaryDecision {
  if (window.exactWindow && (!Number.isFinite(window.start) || !Number.isFinite(window.end) || window.end <= window.start)) return { action: "none" };
  const endTime = window.exactWindow ? window.end : Math.max(window.start + 0.5, window.end);
  const tolerance = window.exactWindow ? Math.min(BOUNDARY_TOLERANCE, (endTime - window.start) / 10) : BOUNDARY_TOLERANCE;
  const beforeStart = state.currentTime < window.start - tolerance;
  const pastEnd = state.currentTime >= endTime - tolerance;
  switch (event) {
    case "timeupdate":
      if (beforeStart) return { action: "snap-start" };
      return pastEnd && !state.paused
        ? { action: "pause-snap" }
        : { action: "none" };
    case "play":
      if (beforeStart) return { action: "snap-start" };
      return state.atEnd || pastEnd
        ? { action: "rewind" }
        : { action: "none" };
    case "seeked":
      if (beforeStart) return { action: "snap-start" };
      return state.currentTime < endTime - tolerance
        ? { action: "clear-at-end" }
        : { action: "none" };
    default:
      return { action: "none" };
  }
}
